import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { datasetsApi, inversionApi, projectsApi } from '../../lib/api';
import { buildWorkflowSteps } from '../../lib/workflow-steps';
import { viewer3dPath } from '../../lib/map-session';
import { useTheme } from '../../contexts/ThemeContext';
import OpenDatasetPopover, { popoverPosBelow } from './OpenDatasetPopover';

/**
 * Workflow panel (the `workflow` tab of the left dock, lib/panel-registry.js): the steps of a
 * resistivity job for the survey in `?dataset=`, each with its state (lib/workflow-steps.js)
 * and a button that opens the panel or page where the step is done. Panels stay where they are.
 *
 * Props: the MapView `all` bag (inversionModels, showGeoreferencing, setShowSectionView,
 * setPseudosection, setShowReportBuilder, setReportType, setShowGeoreferencing, navigate,
 * inversionLinks) plus `datasetId` (the dataset selected in Layers, else ?dataset=), `onTopography(id)` (the in-map Topography dialog), `onOpenViewer3d(datasetId)` (3D (docked)) and `revealPanel(id)`.
 */

const EARTHIMAGER_URL = 'https://erimager-bert.pages.dev/';

const dotColor = (C, state) => (state === 'done' || state === 'available' ? C.okText : state === 'pending' ? C.amber : state === 'failed' ? C.red : C.text3);

function Btn({ C, label, onClick, disabled, testId, btnRef }) {
  return (
    <button ref={btnRef} type="button" onClick={onClick} disabled={disabled} data-testid={testId}
      style={{ fontSize: 10, lineHeight: '16px', padding: '1px 7px', borderRadius: 5, whiteSpace: 'nowrap', cursor: disabled ? 'default' : 'pointer',
        color: disabled ? C.text3 : C.text0, background: C.bg3, border: `1px solid ${C.border}`, opacity: disabled ? 0.55 : 1 }}>
      {label}
    </button>
  );
}

export default function WorkflowPanel({
  datasetId = null, inversionModels = [], showGeoreferencing, inversionLinks, currentProject = null, navigate,
  revealPanel, onTopography, onOpenViewer3d, setShowGeoreferencing, setShowSectionView, setPseudosection, setShowReportBuilder, setReportType,
}) {
  const { C } = useTheme();
  const [dataset, setDataset] = useState(null);
  const [project, setProject] = useState(null);
  const [stored, setStored] = useState([]);
  const [openPos, setOpenPos] = useState(null);
  const openBtnRef = useRef(null);
  const closeOpen = useCallback(() => setOpenPos(null), []);

  const projectId = dataset?.project_id || inversionLinks?.projectId || currentProject?.id || null;
  const modelCount = inversionModels.length;

  // The dataset row: again when the georeference dialog closes (it edits metadata.georef).
  useEffect(() => {
    if (!datasetId) { setDataset(null); return undefined; }
    let live = true;
    datasetsApi.get(datasetId).then((res) => { if (live) setDataset(res?.data || null); }).catch(() => { if (live) setDataset(null); });
    return () => { live = false; };
  }, [datasetId, showGeoreferencing]);

  useEffect(() => {
    if (!projectId) { setProject(null); return undefined; }
    let live = true;
    projectsApi.get(projectId).then((res) => { if (live) setProject(res?.data || null); }).catch(() => { if (live) setProject(null); });
    return () => { live = false; };
  }, [projectId]);

  // Stored models of the dataset; again after a fresh import (the panel's model list grows).
  useEffect(() => {
    if (!datasetId) { setStored([]); return undefined; }
    let live = true;
    inversionApi.list({ ...(projectId ? { project_id: projectId } : {}), dataset_id: datasetId })
      .then((res) => { if (live) setStored(Array.isArray(res?.data) ? res.data : []); })
      .catch(() => { if (live) setStored([]); });
    return () => { live = false; };
  }, [datasetId, projectId, modelCount]);

  const steps = useMemo(() => buildWorkflowSteps({
    dataset: datasetId ? (dataset || { id: datasetId, name: '' }) : null,
    project, storedModels: stored, uiModels: inversionModels,
  }), [datasetId, dataset, project, stored, inversionModels]);

  const go = (path) => () => navigate?.(path);
  const dsPage = datasetId ? `/datasets/${datasetId}` : '/datasets';
  const georef = () => (setShowGeoreferencing ? setShowGeoreferencing(true) : navigate?.(dsPage));
  const buttons = (s) => {
    const off = s.state === 'blocked';
    switch (s.key) {
      case 'upload': return datasetId ? [] : [
        { label: 'Open…', testId: 'workflow-open', ref: openBtnRef, onClick: () => setOpenPos((p) => (p ? null : popoverPosBelow(openBtnRef.current))) },
        { label: 'Upload page', onClick: go('/upload') },
      ];
      case 'qc': return [{ label: 'Open QC', onClick: go(dsPage), disabled: off }];
      case 'georef': return [{ label: 'Georeference', onClick: georef, disabled: off }];
      case 'topo': return [
        { label: 'Topography…', onClick: () => (onTopography ? onTopography(datasetId) : navigate?.(dsPage)), disabled: off },
        ...(onTopography ? [{ label: 'Dataset page', onClick: go(dsPage), disabled: off }] : []),
      ];
      case 'inversion': return [{ label: 'Open EarthImager', onClick: () => window.open(EARTHIMAGER_URL, '_blank', 'noopener'), disabled: off }];
      case 'import': return [{ label: 'Import result', onClick: () => revealPanel?.('inversion'), disabled: off }];
      case 'coords': return [{ label: 'Define coordinates', onClick: () => revealPanel?.('inversion'), disabled: off }];
      case 'view': return [
        { label: 'Section', onClick: () => { setShowSectionView?.(true); revealPanel?.('section'); }, disabled: off },
        { label: 'Pseudosection', onClick: () => { setPseudosection?.({ datasetId }); revealPanel?.('pseudosection'); }, disabled: off },
        { label: '3D', onClick: go(viewer3dPath(projectId, { dataset: datasetId })), disabled: off || !projectId },
        ...(onOpenViewer3d ? [{ label: '3D (docked)', onClick: () => onOpenViewer3d(datasetId), disabled: off }] : []),
      ];
      case 'report': return [{ label: 'Report…', onClick: () => { setReportType?.(null); setShowReportBuilder?.(true); }, disabled: off }];
      default: return [];
    }
  };

  return (
    <div data-testid="workflow-panel" style={{ display: 'flex', flexDirection: 'column', minHeight: 0, overflow: 'auto', background: C.bg2, color: C.text0 }}>
      {datasetId && (
        <div data-testid="workflow-survey" title={dataset?.name || ''}
          style={{ padding: '6px 8px', fontSize: 11, fontWeight: 600, color: C.text1, borderBottom: `1px solid ${C.border}`, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          Survey: {dataset?.name || ''}
        </div>
      )}
      {steps.map((s, i) => (
        <div key={s.key} data-testid={`workflow-step-${s.key}`} data-state={s.state}
          style={{ padding: '7px 8px', borderBottom: `1px solid ${C.border}`, display: 'flex', flexDirection: 'column', gap: 3, opacity: s.state === 'blocked' ? 0.65 : 1 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <span style={{ fontSize: 10, color: C.text3, width: 12, textAlign: 'right', flexShrink: 0 }}>{i + 1}</span>
            <span aria-hidden="true" data-testid="workflow-dot" style={{ width: 8, height: 8, borderRadius: '50%', flexShrink: 0, background: dotColor(C, s.state) }} />
            <span style={{ fontSize: 12, fontWeight: 600, flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: s.state === 'failed' ? C.red : C.text0 }}>{s.title}</span>
          </div>
          <div style={{ fontSize: 11, color: C.text2, paddingLeft: 32, lineHeight: 1.3 }}>{s.detail}</div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, paddingLeft: 32 }}>
            {buttons(s).map((b) => (
              <Btn key={b.label} C={C} label={b.label} onClick={b.onClick} disabled={b.disabled} testId={b.testId} btnRef={b.ref} />
            ))}
          </div>
        </div>
      ))}
      {openPos && <OpenDatasetPopover pos={openPos} wrapRef={openBtnRef} onClose={closeOpen} />}
    </div>
  );
}
